// case-studies/pages/agentforge.js
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import "../styles/aitech.css";

import example1 from "../assets/agentforge/AGENTFORGE.gif";
import example2 from "../assets/agentforge/forge1.jpg";
import example3 from "../assets/agentforge/forgewalkthrough.gif";
import example4 from "../assets/agentforge/a2a.jpg";
import example5 from "../assets/agentforge/architecture.png";
import example6 from "../assets/agentforge/forgebuild.png";

const titleVariant = {
  hidden: { opacity: 0, y: -28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const textVariant = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const imageVariant = {
  hidden: { opacity: 0, scale: 1.18 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const AgentForge = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, []);

  return (
    <main className="ai-root">
      {/* HEADER */}
      <header className="ai-header">
        <div className="ai-header-inner">
          <div className="ai-title-block">
            <motion.h1
              className="ai-title"
              variants={titleVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
            >
              WHEN AI AGENTS STOPPED BEING TOYS
            </motion.h1>

            <motion.p
              className="ai-subtitle"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
            >
              From AI marketplace to autonomous agent ecosystem, building the
              foundations for what comes next.
            </motion.p>
          </div>

          <div className="ai-meta-row">
            <motion.div
              className="ai-meta-line"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            />
            <motion.div
              className="ai-meta-items"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <div className="ai-meta-item">
                <span className="ai-meta-label">Project Type</span>
                <span className="ai-meta-value">
                  AI agent platform and ecosystem
                </span>
              </div>
              <div className="ai-meta-item">
                <span className="ai-meta-label">Role</span>
                <span className="ai-meta-value">
                  Technical lead and platform architect
                </span>
              </div>
              <div className="ai-meta-item">
                <span className="ai-meta-label">Year</span>
                <span className="ai-meta-value">2025</span>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* BANNER */}
      <section className="ai-banner">
        <div className="ai-banner-inner">
          <motion.img
            src={example1}
            alt="Agent Forge platform visual"
            className="ai-banner-image"
            variants={imageVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          />
          <a
            href="https://aitech.io/agentforge"
            className="ai-main-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit Agent Forge
          </a>
        </div>
      </section>

      {/* 1. HOOK */}
      <section className="ai-section ai-section-full">
        <div className="ai-section-inner">
          <motion.h2
            className="ai-section-title ai-section-title-center"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            The Hook
          </motion.h2>

          <motion.div
            className="ai-section-body ai-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              When I first joined AITECH, the vision was straightforward. Build
              an AI Marketplace where people could discover and use AI tools.
            </p>
            <p>Then the industry changed.</p>
            <p>
              Almost overnight, the conversation moved away from individual
              models and toward{" "}
              <span className="ai-emphasis-inline">agents</span>. Not chatbots.
              Not prompts. Systems capable of making decisions, using tools,
              collaborating with other systems and operating with increasing
              levels of autonomy.
            </p>
            <p>
              It became obvious that a marketplace alone would not be enough.
            </p>
            <p>
              The platform needed to evolve alongside the industry.
            </p>
            <p>
              That evolution became{" "}
              <span className="ai-emphasis-inline">Agent Forge</span>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. CONTEXT */}
      <section className="ai-section ai-section-split ai-split-text-left">
        <div className="ai-section-inner ai-section-inner-split">
          <motion.h2
            className="ai-section-title ai-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            Context
          </motion.h2>

          <div className="ai-split-col ai-split-col-media">
            <div className="ai-image-frame">
              <motion.img
                src={example2}
                alt="Agent Forge ecosystem context"
                className="ai-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="ai-split-col ai-split-col-text">
            <motion.div
              className="ai-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                Agent Forge started as a product initiative but quickly became
                something much larger. It was not designed to be another workflow
                builder or another AI agent platform. The goal was to create an
                ecosystem where people could{" "}
                <span className="ai-emphasis-inline">
                  build, deploy, orchestrate and eventually monetize autonomous
                  AI systems
                </span>{" "}
                from a single environment.
              </p>
              <p>That meant connecting a lot of moving pieces together.</p>
              <p>
                Workflows. Knowledge bases. AI providers. Enterprise
                integrations. Storage. Authentication. Billing. Scheduling.
                Notifications. Real-time collaboration.
              </p>
              <p>
                Each feature solved a different problem. The challenge was making
                them feel like they belonged to the same platform.
              </p>
              <p>
                As the scope grew, so did my role. What began as software
                development gradually expanded into{" "}
                <span className="ai-emphasis-inline">
                  architecture, technical leadership, infrastructure planning
                  and product strategy
                </span>
                .
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. THE CHALLENGE */}
      <section className="ai-section ai-section-split ai-split-text-right">
        <div className="ai-section-inner ai-section-inner-split">
          <motion.h2
            className="ai-section-title ai-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            The Challenge
          </motion.h2>

          <div className="ai-split-col ai-split-col-media">
            <div className="ai-image-frame">
              <motion.img
                src={example3}
                alt="Platform architecture challenge"
                className="ai-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="ai-split-col ai-split-col-text">
            <motion.div
              className="ai-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                Building Agent Forge was less about creating features and more
                about creating{" "}
                <span className="ai-emphasis-inline">foundations</span>.
              </p>
              <p>Every decision affected ten others.</p>
              <p>
                The platform needed to support visual workflow creation while
                also supporting advanced runtime execution. It needed to work for
                non-technical users while remaining flexible enough for
                developers. It needed to integrate with dozens of external
                systems without becoming impossible to maintain.
              </p>
              <p>Nothing existed in isolation.</p>
              <p>
                Authentication touched billing. Billing touched workflows.
                Workflows touched AI providers. AI providers touched knowledge
                retrieval.
              </p>
              <p>
                Every component became part of a much larger system. The challenge
                was not building individual pieces. The challenge was{" "}
                <span className="ai-emphasis-inline">
                  designing an architecture that allowed all of those pieces to
                  grow together
                </span>
                .
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. STRATEGY */}
      <section className="ai-section ai-section-full">
        <div className="ai-section-inner">
          <motion.h2
            className="ai-section-title ai-section-title-center"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Strategy
          </motion.h2>

          <motion.div
            className="ai-section-body ai-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>The approach was simple.</p>
            <p>
              Build the platform around{" "}
              <span className="ai-emphasis-inline">extensibility</span>.
            </p>
            <p>
              Instead of creating a closed ecosystem, we focused on making Agent
              Forge adaptable. Over time the platform grew to support more than
              sixty integrations across productivity, cloud and enterprise
              platforms. Users could connect tools, automate processes and build
              increasingly sophisticated systems without needing to write large
              amounts of code.
            </p>
            <p>
              One of the most important decisions was embracing{" "}
              <span className="ai-emphasis-inline">
                Model Context Protocol integrations
              </span>
              .
            </p>
            <p>
              Traditional API integrations work, but they require every
              connection to be built individually. MCP introduced a more
              standardized way for AI systems to discover and interact with tools
              dynamically.
            </p>
            <p>
              It felt like a glimpse of where the industry was heading. Less
              hardcoded behavior. More intelligent systems capable of
              understanding the environment around them.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 5. THE VISION */}
      <section className="ai-section ai-section-split ai-split-text-left">
        <div className="ai-section-inner ai-section-inner-split">
          <motion.h2
            className="ai-section-title ai-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            The Vision
          </motion.h2>

          <div className="ai-split-col ai-split-col-media">
            <div className="ai-image-frame">
              <motion.img
                src={example4}
                alt="Agent-to-agent commerce vision"
                className="ai-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="ai-split-col ai-split-col-text">
            <motion.div
              className="ai-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                The most ambitious part of Agent Forge was never workflow
                automation.
              </p>
              <p>
                It was{" "}
                <span className="ai-emphasis-inline">
                  agent-to-agent commerce
                </span>
                .
              </p>
              <p>
                Most AI platforms treat agents as assistants. Useful, but
                ultimately dependent on human direction.
              </p>
              <p>We wanted to explore a different idea.</p>
              <p>
                What happens when agents can own identities, establish
                reputation, provide services and receive payment for their work?
              </p>
              <p>
                Through integrations with{" "}
                <span className="ai-emphasis-inline">
                  ERC8004 registries and x402 payment infrastructure
                </span>
                , we began building toward a future where agents could
                participate in economic systems rather than simply operate inside
                them.
              </p>
              <p>
                It was a complicated problem. Identity, payments, workflows,
                ownership and external integrations all needed to function
                together without creating friction.
              </p>
              <p>
                But it was also one of the first times I worked on something that
                genuinely felt ahead of the curve.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. LEADERSHIP */}
      <section className="ai-section ai-section-split ai-split-text-right">
        <div className="ai-section-inner ai-section-inner-split">
          <motion.h2
            className="ai-section-title ai-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            Leadership
          </motion.h2>

          <div className="ai-split-col ai-split-col-media">
            <div className="ai-image-frame">
              <motion.img
                src={example5}
                alt="Leadership and strategic collaborations"
                className="ai-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="ai-split-col ai-split-col-text">
            <motion.div
              className="ai-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>As the platform expanded, so did the responsibilities around it.</p>
              <p>
                I led a team consisting of two interns and a senior developer
                while working directly with leadership on product direction and
                technical strategy. Following a leadership transition, I assumed
                greater ownership across{" "}
                <span className="ai-emphasis-inline">
                  architecture, infrastructure planning and delivery
                </span>
                .
              </p>
              <p>
                The role also introduced me to people and organizations operating
                at a scale I had not experienced before.
              </p>
              <p>
                I worked alongside teams from{" "}
                <span className="ai-emphasis-inline">
                  Binance, OKX, DigitalOcean and SambaNova Systems
                </span>{" "}
                on integrations, ecosystem initiatives and strategic
                collaborations.
              </p>
              <p>
                The conversations became bigger. The decisions became bigger. The
                expectations became bigger.
              </p>
              <p>And I had to grow alongside them.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 7. OUTCOME */}
      <section className="ai-section ai-section-split ai-split-text-left">
        <div className="ai-section-inner ai-section-inner-split">
          <motion.h2
            className="ai-section-title ai-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Outcome
          </motion.h2>

          <div className="ai-split-col ai-split-col-media">
            <div className="ai-image-frame">
              <motion.img
                src={example6}
                alt="Agent Forge platform outcome"
                className="ai-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="ai-split-col ai-split-col-text">
            <motion.div
              className="ai-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
            >
              <p>
                Today, Agent Forge stands as the{" "}
                <span className="ai-emphasis-inline">
                  largest platform initiative I have led
                </span>
                .
              </p>
              <p>
                It combines workflows, AI agents, knowledge systems, enterprise
                integrations, cloud infrastructure, billing systems and emerging
                agent commerce into a single ecosystem.
              </p>
              <p>
                More importantly, it represents the point where my career stopped
                being purely about software development.
              </p>
              <p>
                Building features teaches you how to solve problems. Building
                platforms teaches you how systems behave.
              </p>
              <p>
                Leading Agent Forge forced me to think beyond individual
                applications and start thinking about architecture, scalability,
                business strategy and long-term vision at the same time.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 8. REFLECTION */}
      <section className="ai-section ai-section-full ai-section-reflection">
        <div className="ai-section-inner">
          <motion.h2
            className="ai-section-title ai-section-title-left"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Reflection
          </motion.h2>

          <motion.div
            className="ai-section-body ai-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              Agent Forge taught me that{" "}
              <span className="ai-emphasis-inline">
                technical leadership is not about knowing the answer to every
                problem
              </span>
              .
            </p>
            <p>
              It is about creating enough clarity that other people can move
              forward with confidence.
            </p>
            <p>
              The bigger a platform becomes, the less important individual
              features are. What matters is whether the foundations are strong
              enough to support ideas that do not exist yet.
            </p>
            <p>That was the lesson hidden inside Agent Forge.</p>
            <p>
              Build for today's requirements and you create a product. Build
              for tomorrow's possibilities and you create a platform.
            </p>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default AgentForge;
